import React from "react";
import { BuildingIcon, Loader2Icon, UsersIcon } from "lucide-react";
import { isAllBusiness } from "@/api";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { SettingsUsersTable } from "./SettingsUsersTable";
import { UsersSettingsTab, UsersSettingsTabs } from "./tabs";
import { UserRowModel } from "./SettingsUsersTable";
import { useT } from "@/i18n";

type UserContentProps = {
  selectedTab: UsersSettingsTab;
  businessId?: string;
  users: UserRowModel[];
  inactiveFoodicsAccounts: UserRowModel[];
  usersQuery: { isPending: boolean };
  inactiveFoodicsAccountsQuery: { isPending: boolean };
};

function LoadingContent() {
  const { t } = useT();
  return (
    <div
      className="flex items-center justify-center py-20"
      role="status"
      aria-label={t("common.loading")}
    >
      <Loader2Icon aria-hidden="true" className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function EmptyContent({ icon, children }: React.PropsWithChildren<{ icon: React.ReactNode }>) {
  return (
    <div className="flex min-h-80 flex-1 items-center justify-center px-3 pb-4 md:px-4">
      <Empty className="max-w-md border-0 p-0">
        <EmptyHeader>
          <EmptyMedia variant="icon">{icon}</EmptyMedia>
          <EmptyTitle>{children}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    </div>
  );
}

export function UserContent({
  selectedTab,
  businessId,
  users,
  inactiveFoodicsAccounts,
  usersQuery,
  inactiveFoodicsAccountsQuery,
}: UserContentProps) {
  const { t } = useT();
  if (selectedTab === UsersSettingsTabs.inactive) {
    if (!businessId) {
      return null;
    }
    if (isAllBusiness(businessId)) {
      return <EmptyContent icon={<BuildingIcon />}>{t("users.chooseSingleBusiness")}</EmptyContent>;
    }
    if (inactiveFoodicsAccountsQuery.isPending) {
      return <LoadingContent />;
    }
    return inactiveFoodicsAccounts.length === 0 ? (
      <EmptyContent icon={<UsersIcon />}>{t("users.noFoodicsAccounts")}</EmptyContent>
    ) : (
      <SettingsUsersTable
        key={businessId}
        accounts={inactiveFoodicsAccounts}
        selectedTab={selectedTab}
        businessId={businessId}
      />
    );
  }

  if (usersQuery.isPending) {
    return <LoadingContent />;
  }

  return users.length === 0 ? (
    <EmptyContent icon={<UsersIcon />}>{t("users.noBIAccounts")}</EmptyContent>
  ) : (
    <SettingsUsersTable accounts={users} selectedTab={selectedTab} businessId={businessId} />
  );
}
